import React from 'react'
import { FormGroup } from '../types/CustomForm'
import { AnswerState } from '../components/DynamicForm'
import {
  TEAM_OFFICIALS_SECTION,
  PLAYER_INFO_SECTION,
  TEAM_OFFICIAL_FIELDS,
  PLAYER_INFO_FIELDS,
  matchesReservedField,
  ReservedField,
} from '../constants/prefillMappings'

/**
 * 🧹 clearReservedAnswers: Clears all reserved form answers (except Email) when user chooses "Create new Player / Team Official"
 *
 * This function removes pre-filled values from reserved form fields when the user switches
 * from selecting an existing player/team official to creating a new one. It preserves the
 * email field since it should remain pre-filled with the logged user's email.
 *
 * @param answers - Current answer map (key = formItem.id, value = field value)
 * @param selectedForm - The full form definition (may be null/undefined)
 * @param isTeamRep - true → use TEAM_OFFICIAL_FIELDS, false → use PLAYER_INFO_FIELDS
 * @param setAnswers - React setState helper returned by useState<AnswerState>()
 *
 * @example
 * ```typescript
 * const [answers, setAnswers] = useState<AnswerState>({});
 *
 * const handleSelectPlayer = (playerId: string) => {
 *   if (playerId === 'new') {
 *     clearReservedAnswers(answers, selectedForm, isTeamRep, setAnswers);
 *   } else {
 *     // normal flow
 *   }
 * };
 * ```
 *
 * @since Registration v2 - Dynamic form reserved field management
 */
export function clearReservedAnswers(
  answers: AnswerState,
  selectedForm: { groups: FormGroup[] } | null,
  isTeamRep: boolean,
  setAnswers: React.Dispatch<React.SetStateAction<AnswerState>>
): void {
  // Early return if no form definition
  if (!selectedForm?.groups?.length) {
    return
  }

  // Determine which section and fields to look for based on registration type
  const targetSectionName = isTeamRep
    ? TEAM_OFFICIALS_SECTION
    : PLAYER_INFO_SECTION
  const fieldMappings = isTeamRep ? TEAM_OFFICIAL_FIELDS : PLAYER_INFO_FIELDS

  // Find the target reserved section
  const targetSection = selectedForm.groups.find(
    (group) => group.is_reserved === 1 && group.name === targetSectionName
  )

  if (!targetSection) {
    return
  }

  // Build a Set of form item IDs to be cleared (excluding email)
  const idsToClear = new Set<string>()

  fieldMappings.forEach((mapping: ReservedField) => {
    // Skip email field - it should remain pre-filled
    if (mapping.playerKey === 'email') {
      return
    }

    // Find the corresponding form item. Matched the same tolerant way the
    // prefill hook matches, so a form using entity-qualified names ("Team
    // Official First Name") clears the same fields it fills — a strict match
    // here would leave the previous person's name behind.
    const formItem = targetSection.items.find(
      (item) =>
        item.is_reserved === 1 &&
        matchesReservedField(item.name, mapping.fieldName)
    )

    if (!formItem) {
      return
    }

    // Add item ID to the set for clearing
    idsToClear.add(formItem.id)
  })

  // Generate cleaned answers by removing the identified fields
  const cleanedAnswers = { ...answers }
  idsToClear.forEach((id) => {
    delete cleanedAnswers[id]
  })

  // Update the answers state
  setAnswers(cleanedAnswers)
}
